import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import Cropper from 'react-easy-crop';
import Slider from 'react-slider';
import { getCroppedImg } from 'utils/cropUtils';

/**
 * Props:
 * - imageSrc: string (dataURL)
 * - aspect: number (default 1)
 * - shape: 'rect' | 'round' (default 'round')
 * - onCropSave: (Blob | File | dataURL) => void
 * - onClose: () => void
 * - framePadding: number px (default 12) visual padding inside container
 */
const ImageCropper = ({
  imageSrc,
  aspect = 1,
  shape = 'round',
  onCropSave,
  onClose,
  framePadding = 12,
  cornerRadiusPx = 16,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [minZoom, setMinZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const cropContainerRef = useRef(null);

  const [containerSize, setContainerSize] = useState({
    width: 256,
    height: 256,
  });

  // Re-center on new image
  useEffect(() => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  }, [imageSrc]);

  // Track container size
  useEffect(() => {
    const update = () => {
      if (!cropContainerRef.current) return;
      setContainerSize({
        width: cropContainerRef.current.offsetWidth,
        height: cropContainerRef.current.offsetHeight,
      });
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // Compute the cropping frame (width/height) inside the container.
  // Keeps the frame within the smallest container dimension with some padding.
  const frame = useMemo(() => {
    const maxSide =
      Math.min(containerSize.width, containerSize.height) - framePadding * 2;

    // Try width-constrained by default
    let width = Math.max(120, Math.floor(maxSide));
    let height = Math.floor(width / aspect);

    // If height overflows, constrain by height instead
    if (height > maxSide) {
      height = Math.max(120, Math.floor(maxSide));
      width = Math.floor(height * aspect);
    }

    return { width, height };
  }, [containerSize, aspect, framePadding]);

  // Ensure image fully covers the crop frame at zoom = minZoom
  const handleMediaLoaded = useCallback(
    ({ naturalWidth, naturalHeight }) => {
      const { width: cw, height: ch } = containerSize;
      const { width: fw, height: fh } = frame;
      if (!naturalWidth || !naturalHeight || !cw || !ch) return;

      // Base scale when zoom=1 with objectFit="contain"
      const baseScale = Math.min(cw / naturalWidth, ch / naturalHeight);

      const needW = fw / (naturalWidth * baseScale);
      const needH = fh / (naturalHeight * baseScale);
      const computedMin = Math.max(1, needW, needH);

      setMinZoom(computedMin);
      setZoom((z) => Math.max(z, computedMin));
      setCrop({ x: 0, y: 0 }); // center

      // Initial crop rect in pixels so Save works without user interaction
      const targetW = fw / baseScale;
      const targetH = fh / baseScale;
      const x = Math.max(0, (naturalWidth - targetW) / 2);
      const y = Math.max(0, (naturalHeight - targetH) / 2);
      setCroppedAreaPixels({
        x: Math.round(x),
        y: Math.round(y),
        width: Math.round(targetW),
        height: Math.round(targetH),
      });
    },
    [containerSize, frame]
  );

  const onCropComplete = useCallback((_, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleSaveClick = useCallback(async () => {
    if (!croppedAreaPixels) return;
    try {
      const blob = await getCroppedImg(imageSrc, croppedAreaPixels);
      onCropSave(blob);
    } catch (e) {
      console.error('Error during cropping:', e);
    }
  }, [croppedAreaPixels, imageSrc, onCropSave]);

  // ESC closes
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="relative bg-white p-6 rounded-lg shadow-lg">
        <div
          ref={cropContainerRef}
          className="relative w-64 h-64 overflow-hidden"
        >
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            minZoom={minZoom}
            maxZoom={8}
            aspect={aspect}
            cropShape={shape} // 'rect' | 'round'
            cropSize={frame} // { width, height }
            showGrid={false}
            restrictPosition
            objectFit="contain"
            onMediaLoaded={handleMediaLoaded}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            style={{
              containerStyle: { width: '100%', height: '100%' },
              // Make react-easy-crop's own crop area honor the rounded corners
              // when shape === 'rect'. Avoids drawing a second rectangular ring
              // on top of the library's default sharp-cornered border.
              cropAreaStyle:
                shape === 'rect'
                  ? {
                      borderRadius: cornerRadiusPx,
                      border: '2px solid rgba(255,255,255,0.85)',
                    }
                  : undefined,
            }}
          />
        </div>

        <div className="mt-4 pb-4">
          <Slider
            min={minZoom}
            max={8}
            step={0.05}
            value={zoom}
            onChange={setZoom}
            className="w-full"
            thumbClassName="slider-thumb"
            trackClassName="slider-track"
            ariaLabel={['Zoom']}
          />
        </div>

        <div className="flex justify-end gap-2 mt-2">
          <button
            type="button"
            className="bg-gray-300 px-4 py-2 rounded-md"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="bg-yellow-500 text-white px-4 py-2 rounded-md"
            onClick={handleSaveClick}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageCropper;
